import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useDisconnect } from 'wagmi';

import { joinRewardPool } from '@shared/api/rewardPools';
import {
  REWARD_POOL_ACCESS_POLICY_OPERATOR,
  RewardPool,
  RewardPoolTicketCurrency,
} from '@shared/api/types/rewardPools';
import { normalizeError } from '@shared/helpers/displayErrorToast';
import { getErrorMessage } from '@shared/helpers/error';
import {
  getIsSelfJoinedRewardPoolQueryOptions,
  getSelfRewardPoolLeaderboardDataQueryOptions,
  rewardPoolsQueryKeys,
  useRewardPoolAccessPoliciesQuery,
} from '@shared/hooks/query/rewardPools';

import { incrementPoolCounter } from './incrementPoolCounter';
import { SignProps, useSignatureProps } from './useSignatureProps';

export type JoinRewardPoolProps =
  | {
      rewardPool: RewardPool;
      entryAmount: number;
      entryCurrency: RewardPoolTicketCurrency;
      signMessage: string;
    }
  | {
      rewardPool: RewardPool;
      entryAmount?: never;
      entryCurrency?: never;
      signMessage?: never;
    };

export const useJoinRewardPool = (rewardPoolId: number) => {
  const { disconnectAsync } = useDisconnect();
  const { refetch } = useRewardPoolAccessPoliciesQuery(rewardPoolId, {
    enabled: false,
  });

  const queryClient = useQueryClient();
  const { getSignProps } = useSignatureProps();

  return useMutation({
    mutationFn: async ({
      rewardPool,
      entryCurrency,
      entryAmount,
      signMessage = '',
    }: JoinRewardPoolProps) => {
      const handleSignResult = async (signProps?: SignProps) => {
        if (!signProps) {
          return;
        }

        try {
          await joinRewardPool({
            rewardPoolId: rewardPool.id,
            data: {
              ...signProps,
              entryCurrency,
              entryAmount,
            },
          });
        } catch (error) {
          const errorMessage = getErrorMessage(error);

          if (errorMessage.includes('access policies')) {
            throw new Error('Please complete all pool requirement before joining the pool.');
          }

          throw error;
        }
      };

      const { data: accessPolicies, error: accessPoliciesError } = await refetch();

      if (accessPoliciesError) {
        throw accessPoliciesError;
      }

      if (!accessPolicies) {
        throw new Error("We couldn't verify if the requirements were met. Please try again.");
      }

      if (rewardPool.access_policy.operator === REWARD_POOL_ACCESS_POLICY_OPERATOR.AND) {
        const isEveryRequirementMet = accessPolicies.every(({ qualified }) => qualified);

        if (!isEveryRequirementMet) {
          throw new Error('Please make sure all pool requirements are met.');
        }
      } else {
        const isSomeRequirementMet = accessPolicies.some(({ qualified }) => qualified);

        if (!isSomeRequirementMet) {
          throw new Error('Please ensure at least one of the pool requirement is met.');
        }
      }

      try {
        const DEFAULT_MESSAGE = `Join ${rewardPool.name} Reward Pool at Magic Store`;
        const signProps = await getSignProps(signMessage || DEFAULT_MESSAGE);
        await handleSignResult(signProps);
      } catch (error) {
        const normalizedError = normalizeError(error);

        if (normalizedError.message === 'Account not provided') {
          await disconnectAsync();
        }

        throw normalizedError;
      }
    },
    onSuccess: (_, { rewardPool }) => {
      queryClient.setQueryData(getIsSelfJoinedRewardPoolQueryOptions().queryKey, (data) => {
        if (!data) {
          return {
            pools: [],
            myPoolIds: [rewardPool.id],
          };
        }

        return {
          ...data,
          myPoolIds: [...data.myPoolIds, rewardPool.id],
        };
      });

      void incrementPoolCounter({
        poolId: rewardPool.id,
        key: 'participants_counter',
        increment: +1,
        queryClient,
      });

      queryClient.invalidateQueries({
        queryKey: rewardPoolsQueryKeys.rewardPool(rewardPool.id),
      });

      queryClient.invalidateQueries({
        queryKey: rewardPoolsQueryKeys.rewardPoolLeaderboardInfinite(rewardPool.id),
      });

      queryClient.invalidateQueries({
        queryKey: getSelfRewardPoolLeaderboardDataQueryOptions(rewardPool.id).queryKey,
      });
    },
  });
};
